/**
 * Responsive Utilities for Web
 * Enhanced responsive design utilities for desktop/tablet layouts
 */

import { Dimensions, Platform } from 'react-native';

// Base responsive functions for web
export const widthPercentageToDP = (widthPercent) => {
  const screenWidth = typeof window !== 'undefined' ? window.innerWidth : 375;
  return (widthPercent * screenWidth) / 100;
};

export const heightPercentageToDP = (heightPercent) => {
  const screenHeight = typeof window !== 'undefined' ? window.innerHeight : 667;
  return (heightPercent * screenHeight) / 100;
};

// Aliases
export const wp = widthPercentageToDP;
export const hp = heightPercentageToDP;

// Breakpoints
export const BREAKPOINTS = {
  mobile: 768,
  tablet: 1024,
  desktop: 1200,
  largeDesktop: 1440,
};

// Get current screen size
export const getScreenSize = () => {
  const width = typeof window !== 'undefined' ? window.innerWidth : 375;
  
  if (width >= BREAKPOINTS.largeDesktop) return 'largeDesktop';
  if (width >= BREAKPOINTS.desktop) return 'desktop';
  if (width >= BREAKPOINTS.tablet) return 'tablet';
  return 'mobile';
};

// Check if current platform is web
export const isWeb = Platform.OS === 'web';

// Enhanced responsive width
export const responsiveWidth = (percentage) => {
  const screenSize = getScreenSize();
  
  // Adjust percentages based on screen size
  switch (screenSize) {
    case 'largeDesktop':
      return wp(percentage * 0.6); // Constrain width on very large screens
    case 'desktop':
      return wp(percentage * 0.7);
    case 'tablet':
      return wp(percentage * 0.85);
    default:
      return wp(percentage);
  }
};

// Enhanced responsive height
export const responsiveHeight = (percentage) => {
  return hp(percentage);
};

// Container width for different screen sizes
export const getContainerWidth = () => {
  const screenSize = getScreenSize();
  
  switch (screenSize) {
    case 'largeDesktop':
      return '60%';
    case 'desktop':
      return '70%';
    case 'tablet':
      return '85%';
    default:
      return '100%';
  }
};

// Max width for content
export const getMaxContentWidth = () => {
  const screenSize = getScreenSize();
  
  switch (screenSize) {
    case 'largeDesktop':
      return 1200;
    case 'desktop':
      return 1000;
    case 'tablet':
      return 800;
    default:
      return '100%';
  }
};

// Grid columns based on screen size
export const getGridColumns = () => {
  const screenSize = getScreenSize();
  
  switch (screenSize) {
    case 'largeDesktop':
      return 4;
    case 'desktop':
      return 3;
    case 'tablet':
      return 2;
    default:
      return 1;
  }
};

// Responsive font sizes
export const getFontSize = (size) => {
  const screenSize = getScreenSize();
  const multiplier = {
    largeDesktop: 1.1,
    desktop: 1.05,
    tablet: 1.0,
    mobile: 1.0,
  }[screenSize];
  
  return size * multiplier;
};

// Responsive spacing
export const getSpacing = (size) => {
  const screenSize = getScreenSize();
  const multiplier = {
    largeDesktop: 1.2,
    desktop: 1.1,
    tablet: 1.0,
    mobile: 1.0,
  }[screenSize];
  
  return size * multiplier;
};

// Common responsive styles
export const responsiveStyles = {
  container: {
    width: getContainerWidth(),
    maxWidth: getMaxContentWidth(),
    alignSelf: 'center',
    paddingHorizontal: getSpacing(20),
  },
  
  gridContainer: {
    flexDirection: getScreenSize() === 'mobile' ? 'column' : 'row',
    flexWrap: 'wrap',
    justifyContent: 'space-between',
  },
  
  gridItem: {
    width: getScreenSize() === 'mobile' 
      ? '100%' 
      : `${100 / getGridColumns() - 2}%`,
    marginBottom: getSpacing(20),
  },
  
  sidebar: {
    width: getScreenSize() === 'mobile' ? '100%' : '250px',
    display: getScreenSize() === 'mobile' ? 'none' : 'flex',
  },
  
  mainContent: {
    flex: 1,
    marginLeft: getScreenSize() === 'mobile' ? 0 : 250,
  },
  
  card: {
    padding: getSpacing(16),
    marginVertical: getSpacing(8),
    borderRadius: 12,
    backgroundColor: '#2a2a3e',
  },
  
  button: {
    paddingHorizontal: getSpacing(20),
    paddingVertical: getSpacing(12),
    borderRadius: 8,
    minHeight: 48, // Accessibility standard
  },
  
  input: {
    paddingHorizontal: getSpacing(16),
    paddingVertical: getSpacing(12),
    borderRadius: 8,
    minHeight: 48, // Accessibility standard
  },
};

// Media query style helper
export const createMediaQuery = (styles) => {
  const screenSize = getScreenSize();
  return styles[screenSize] || styles.default || {};
};

// Hook for responsive values
export const useResponsive = () => {
  const screenSize = getScreenSize();
  const { width, height } = Dimensions.get('window');
  
  return {
    screenSize,
    width,
    height,
    isMobile: screenSize === 'mobile',
    isTablet: screenSize === 'tablet',
    isDesktop: screenSize === 'desktop' || screenSize === 'largeDesktop',
    isWeb,
    responsiveWidth,
    responsiveHeight,
    getContainerWidth,
    getMaxContentWidth,
    getGridColumns,
    getFontSize,
    getSpacing,
  };
};

// Layout helpers
export const Layout = {
  // Flexbox helpers
  center: {
    alignItems: 'center',
    justifyContent: 'center',
  },
  
  spaceBetween: {
    justifyContent: 'space-between',
  },
  
  spaceAround: {
    justifyContent: 'space-around',
  },
  
  spaceEvenly: {
    justifyContent: 'space-evenly',
  },
  
  // Responsive layout components
  row: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  
  column: {
    flexDirection: 'column',
  },
  
  // Common shadows for web
  shadow: isWeb ? {
    boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
  } : {
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.1,
    shadowRadius: 4,
    elevation: 3,
  },
  
  // Border radius
  rounded: {
    borderRadius: 8,
  },
  
  roundedLarge: {
    borderRadius: 16,
  },
  
  // Padding helpers
  padding: (size) => ({
    padding: getSpacing(size),
  }),
  
  paddingHorizontal: (size) => ({
    paddingHorizontal: getSpacing(size),
  }),
  
  paddingVertical: (size) => ({
    paddingVertical: getSpacing(size),
  }),
  
  // Margin helpers
  margin: (size) => ({
    margin: getSpacing(size),
  }),
  
  marginHorizontal: (size) => ({
    marginHorizontal: getSpacing(size),
  }),
  
  marginVertical: (size) => ({
    marginVertical: getSpacing(size),
  }),
};

// Export size-matters compatible functions
export const ms = (size, factor = 0.5) => size + (size * factor - size);
export const vs = (size) => hp(size / 8); // verticalScale equivalent
export const spacing = getSpacing;
export const fontSize = getFontSize;
export const iconSize = (size) => ms(size);
export const borderRadius = (size) => size;

export default {
  BREAKPOINTS,
  getScreenSize,
  isWeb,
  responsiveWidth,
  responsiveHeight,
  getContainerWidth,
  getMaxContentWidth,
  getGridColumns,
  getFontSize,
  getSpacing,
  responsiveStyles,
  createMediaQuery,
  useResponsive,
  Layout,
  ms,
  vs,
  spacing,
  fontSize,
  iconSize,
  borderRadius,
};